import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import LogoImage from "../components/LogoImage";

function highlight(text, q) {
    if (!q) return text;
    try {
        const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
        const parts = text.split(re);
        return parts.map((part, i) =>
            re.test(part) ? (
                <mark
                    key={i}
                    className="bg-saffron-100 text-saffron-700 rounded px-0.5"
                >
                    {part}
                </mark>
            ) : (
                <React.Fragment key={i}>{part}</React.Fragment>
            ),
        );
    } catch {
        return text;
    }
}

export default function SearchPage() {
    const [params, setParams] = useSearchParams();
    const initial = params.get("q") || "";
    const [q, setQ] = useState(initial);
    const [results, setResults] = useState({ groups: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [popular, setPopular] = useState([]);

    useEffect(() => {
        api.listCategories("count").then((c) => setPopular(c.slice(0, 6)));
    }, []);

    useEffect(() => {
        const term = (params.get("q") || "").trim();
        if (!term) {
            setResults({ groups: [], total: 0 });
            return;
        }
        setLoading(true);
        api.search(term)
            .then(setResults)
            .catch(() => setResults({ groups: [], total: 0 }))
            .finally(() => setLoading(false));
    }, [params]);

    const onSubmit = (e) => {
        e.preventDefault();
        setParams(q.trim() ? { q: q.trim() } : {});
    };

    const term = params.get("q") || "";

    return (
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-12" data-testid="search-page">
            <h1 className="font-heading font-extrabold text-4xl text-ink tracking-tight">
                Search
            </h1>
            <form
                onSubmit={onSubmit}
                className="mt-6 flex items-stretch bg-white border border-line rounded-2xl shadow-sm focus-within:ring-2 focus-within:ring-saffron/30 focus-within:border-saffron/40 overflow-hidden"
            >
                <Search className="self-center ml-4 h-5 w-5 text-ink-muted" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    type="search"
                    placeholder="Search products, categories..."
                    className="flex-1 bg-transparent px-4 py-4 outline-none"
                    data-testid="search-page-input"
                />
                <button
                    type="submit"
                    className="btn-saffron px-5 py-3 m-1.5 rounded-xl text-sm font-semibold"
                    data-testid="search-page-submit"
                >
                    Search
                </button>
            </form>

            {term && (
                <p className="mt-4 text-ink-muted">
                    {loading
                        ? "Searching..."
                        : `${results.total} result${results.total === 1 ? "" : "s"} for `}
                    {!loading && (
                        <span className="text-ink font-semibold">"{term}"</span>
                    )}
                </p>
            )}

            {term && !loading && results.total === 0 && (
                <div className="mt-8 bg-white border border-line rounded-2xl p-8 text-center">
                    <p className="text-ink-muted">
                        No verified Indian alternatives found for "{term}".
                    </p>
                    <p className="mt-2 text-ink-muted text-sm">
                        Know of one?{" "}
                        <Link
                            to="/submit"
                            className="text-saffron font-semibold hover:underline"
                        >
                            Submit it →
                        </Link>
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2 justify-center">
                        {popular.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/category/${c.slug}`}
                                className="text-sm font-semibold px-3 py-1.5 rounded-full bg-saffron-50 text-saffron-700 border border-saffron-200 hover:bg-saffron-100"
                            >
                                {c.name}
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-8 space-y-10">
                {results.groups.map((g) => (
                    <section
                        key={g.category_slug}
                        data-testid={`search-group-${g.category_slug}`}
                    >
                        <div className="flex items-end justify-between mb-4">
                            <h2 className="font-heading font-bold text-xl text-ink">
                                {g.category_name}{" "}
                                <span className="text-ink-muted font-medium text-sm">
                                    ({g.products.length})
                                </span>
                            </h2>
                            <Link
                                to={`/category/${g.category_slug}`}
                                className="text-sm font-semibold text-saffron"
                            >
                                View category →
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {g.products.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/product/${p.slug}`}
                                    className="bg-white border border-line rounded-xl p-4 flex gap-3 card-lift"
                                    data-testid={`search-result-${p.slug}`}
                                >
                                    <LogoImage
                                        src={p.logo_url}
                                        domain={p.domain}
                                        name={p.name}
                                        size={44}
                                        rounded="rounded-lg"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-ink truncate">
                                            {highlight(p.name, term)}
                                        </div>
                                        <div className="text-xs text-ink-muted line-clamp-2 mt-0.5">
                                            {highlight(
                                                p.description.slice(0, 140) + "...",
                                                term,
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}
